import type { UserInfo } from "../types/UserTypes";

export const IMPERSONATION_ENABLED =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENABLE_E2E_OVERRIDES === "true";
export const IMPERSONATION_CHANGED = "landagility:impersonation-changed";
export const IMPERSONATION_REJECTED = "landagility:impersonation-rejected";
const DEV_USER_OVERRIDE_KEY = "dev_user_override";

let authenticatedUserId: number | string | null = null;
let identityVersion = 0;

export function getIdentityVersion(): number {
  return identityVersion;
}

export function invalidateIdentityContext(): void {
  identityVersion++;
}

export function setAuthenticatedUserId(userId: number | string | null): void {
  if (authenticatedUserId !== userId) invalidateIdentityContext();
  authenticatedUserId = userId;
}

export function getAuthenticatedUserId(): number | string | null {
  return authenticatedUserId;
}

export function getDevUserOverride(): UserInfo | null {
  if (!IMPERSONATION_ENABLED) return null;
  try {
    const user = JSON.parse(localStorage.getItem(DEV_USER_OVERRIDE_KEY) || "null");
    return user && Number.isSafeInteger(user.userId) && user.userId > 0 ? user : null;
  } catch {
    return null;
  }
}

export function setDevUserOverride(user: UserInfo | null): void {
  if (user && !IMPERSONATION_ENABLED) return;
  if (user) localStorage.setItem(DEV_USER_OVERRIDE_KEY, JSON.stringify(user));
  else localStorage.removeItem(DEV_USER_OVERRIDE_KEY);
  invalidateIdentityContext();
  window.dispatchEvent(new Event(IMPERSONATION_CHANGED));
}

// Impersonation lives in localStorage (shared by tabs). Sync changes made in
// other tabs into this tab's generation guard and context before new requests.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key !== DEV_USER_OVERRIDE_KEY && e.key !== null) return;
    invalidateIdentityContext();
    window.dispatchEvent(new Event(IMPERSONATION_CHANGED));
  });
}

export function isImpersonationRejection(status: number, code?: string): boolean {
  if (status !== 400 && status !== 403 && status !== 404) return false;
  return code === "INVALID_IMPERSONATION_TARGET" || (typeof code === "string" && code.startsWith("IMPERSONATION_"));
}

export function rejectImpersonation(userId: string, version = identityVersion): void {
  if (version !== identityVersion || String(getDevUserOverride()?.userId) !== userId) return;
  setDevUserOverride(null);
  window.dispatchEvent(new Event(IMPERSONATION_REJECTED));
}
